<template>
  <v-dialog v-model="addDialog" class="the-app-dialogs" persistent max-width="1100px">
    <v-card>
      <form @submit.prevent="validateBeforeSubmit($event)">
        <v-layout row class="no-select" style="background-color:#09132f">
          <h3 id="dialog-title" class="text-uppercase px-3 pt-3" style="text-xs-center letter-spacing:2px">
            <v-layout align-center>
              <v-icon light x-large class="rgba(0,0,0,0.3)--text pb-1">attach_money</v-icon>
              <span class="px-3">{{ $t('Add New Sale') }}</span>
            </v-layout>
          </h3>
          <v-spacer />
          <v-card-actions>
            <v-btn fab icon dark @click="$emit('hidemodal')">
              <v-icon>close</v-icon>
            </v-btn>
          </v-card-actions>
        </v-layout>

        <v-card-title class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="headline no-select pb-1">
              <v-layout justify-end></v-layout>
            </v-flex>

            <v-layout row wrap justify-content-end>
              <v-flex xs5 pa-2>
                <v-autocomplete
                  id="sale-focus-me"
                  v-model="form.customer_id_fk"
                  :label="`${$t('Customer')} *`"
                  hide-details
                  :items="fullCustomersList"
                  box
                  data-vv-name="Customer"
                  background-color="white"
                  :error="errors.has('Customer')"
                  v-validate="'required'"
                  @input="changeReceiveTypeToCash(form.customer_id_fk)"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['customers_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-customer')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new customer') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs4 pa-2>
                <v-autocomplete
                  v-model="form.receive_type"
                  :label="`${$t('Receive Type')} *`"
                  hide-details
                  :items="receiveTypes"
                  box
                  data-vv-name="Receive Type"
                  :readonly="form.customer_id_fk === 1"
                  background-color="white"
                  :error="errors.has('Receive Type')"
                  v-validate="'required'"
                />
              </v-flex>

              <v-flex xs2 pt-2 pr-2 pl-2>
                <v-checkbox v-model="form.isFixed" color="blue" :label="$t('Fixed Asset')" />
              </v-flex>

              <v-flex xs5 pa-2>
                <v-autocomplete
                  v-model="form.sale_man_id_fk"
                  :items="fullSaleManList"
                  :label="`${$t('Salesman')}`"
                  box
                  hide-details
                  background-color="white"
                  data-vv-name="Salesman"
                  :menu-props="{ transition: 'slide-y-transition' }"
                />
              </v-flex>

              <v-flex xs1 pt-2 v-if="hasPermissions(['sale_man_store'])">
                <v-tooltip top>
                  <v-btn label @click="$emit('add-salesman')" slot="activator" icon fab small color="green">
                    <v-icon color="white" size="25">add</v-icon>
                  </v-btn>
                  <span>{{ $t('Add new salesmen') }}</span>
                </v-tooltip>
              </v-flex>

              <v-flex xs6 class="pa-2">
                <v-menu v-model="billDateMenu" full-width max-width="290" :close-on-content-click="false">
                  <v-text-field
                    slot="activator"
                    :value="form.bill_date"
                    :label="`${$t('Bill Date')} *`"
                    prepend-inner-icon="event"
                    box
                    readonly
                    hide-details
                    name="Bill Date"
                    background-color="white"
                    autocomplete="off"
                    :error="errors.has('Bill Date')"
                    v-validate="'required|date_format:yyyy-MM-dd'"
                  />
                  <v-date-picker @change="billDateMenu = false" v-model="form.bill_date" />
                </v-menu>
              </v-flex>

              <v-flex xs12 style="margin-top: 20px">
                <v-layout row wrap class="form-table-header">
                  <v-flex class="form-table-content">
                    <v-layout row>
                      <v-flex xs1 pa-2>#</v-flex>
                      <v-flex xs11 pa-2>{{ $t('Product') }}</v-flex>
                    </v-layout>
                  </v-flex>
                  <v-flex xs7 :class="{ xs8: hasCollection && !form.isFixed }" class="form-table-content">
                    <v-layout row>
                      <!--                      <v-flex pa-2 class="form-table-content" shrink>{{ $t('Available Quantity') }}</v-flex>-->
                      <v-flex pa-2 v-if="hasDetail">{{ $t('Detail') }}</v-flex>
                      <v-flex pa-2 v-if="hasCollection && !form.isFixed">{{ $t('Collections') }}</v-flex>
                      <v-flex pa-2>{{ $t('Quantity') }}</v-flex>
                      <v-flex pa-2>{{ `${$t('Price')} (${systemCurrency})` }}</v-flex>
                      <v-flex pa-2>{{ `${$t('Total')} (${systemCurrency})` }}</v-flex>
                    </v-layout>
                  </v-flex>

                  <v-flex xs1 style="padding-top: 3px" class="form-table-content">
                    <v-tooltip top>
                      <v-btn label @click="addRow()" small slot="activator" icon color="green">
                        <v-icon color="white">add</v-icon>
                      </v-btn>
                      <span>{{ $t('Add new row') }}</span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>

                <template v-if="rowNumbers > 0">
                  <v-layout row wrap v-for="(row, index) in rowNumbers" :key="index">
                    <v-flex>
                      <v-layout row>
                        <v-flex xs1 pa-1 style="text-align: center; margin-top: 20px; font-size: 16px">{{
                          index + 1
                        }}</v-flex>
                        <v-flex xs11 pa-1>
                          <v-autocomplete
                            v-model="product_id_fk[index]"
                            :label="`${$t('Product')} *`"
                            :items="form.isFixed ? assetsList : fullSaleProductsList"
                            hide-details
                            box
                            :data-vv-name="'Product' + (index + 1)"
                            background-color="white"
                            :error="errors.has('Product' + (index + 1))"
                            v-validate="'required'"
                            @input="getProductSalePriceAndQuantity(index, product_id_fk[index]), findCollections(index)"
                          />
                        </v-flex>
                      </v-layout>
                    </v-flex>
                    <v-flex xs7 :class="{ xs8: hasCollection && !form.isFixed }">
                      <v-layout row>
                        <!--                        <v-flex pa-1>-->
                        <!--                          <v-text-field-->
                        <!--                            v-model.number="available_quantity[index]"-->
                        <!--                            :label="`${$t('Available Quantity')} *`"-->
                        <!--                            hide-details-->
                        <!--                            box-->
                        <!--                            readonly-->
                        <!--                            :data-vv-name="'Available Quantity' + (index + 1)"-->
                        <!--                            background-color="white"-->
                        <!--                            :error="errors.has('Available Quantity' + (index + 1))"-->
                        <!--                            v-validate="'required|decimal:3'"-->
                        <!--                          />-->
                        <!--                        </v-flex>-->

                        <v-flex pa-1 v-if="hasDetail">
                          <v-text-field
                            v-model.number="sale_detail[index]"
                            :label="`${$t('Detail')} *`"
                            hide-details
                            box
                            :data-vv-name="'Detail' + (index + 1)"
                            background-color="white"
                            :error="errors.has('Detail' + (index + 1))"
                          />
                        </v-flex>

                        <v-flex pa-1 v-if="hasCollection && !form.isFixed">
                          <v-autocomplete
                            v-model="collection_id_fk[index]"
                            :label="$t('Collections')"
                            :items="collections[index]"
                            item-text="collection_name"
                            item-value="collection_id"
                            hide-details
                            box
                            :disabled="collections[index] && collections[index].length === 0"
                            :data-vv-name="'Collections' + (index + 1)"
                            background-color="white"
                            :error="errors.has('Collections' + (index + 1))"
                            @input="collectionPrice(index)"
                          />
                        </v-flex>

                        <v-flex pa-1>
                          <v-text-field
                            v-model.number="sale_quantity[index]"
                            :label="`${$t('Quantity')} *`"
                            hide-details
                            box
                            :data-vv-name="'Quantity' + (index + 1)"
                            background-color="white"
                            :error="errors.has('Quantity' + (index + 1))"
                            v-validate="'required|decimal:3'"
                            @input="calculateTotal(index, sale_quantity[index], sale_price[index])"
                            data-vv-delay="1000"
                          />
                        </v-flex>
                        <v-flex pa-1>
                          <v-text-field
                            v-model.number="sale_price[index]"
                            :label="`${$t('Price')} (${systemCurrency}) *`"
                            hide-details
                            box
                            :data-vv-name="'Price' + (index + 1)"
                            background-color="white"
                            :error="errors.has('Price' + (index + 1))"
                            v-validate="'required|decimal:3'"
                            @input="calculateTotal(index, sale_quantity[index], sale_price[index])"
                            data-vv-delay="1000"
                          />
                        </v-flex>
                        <v-flex pa-1>
                          <v-text-field
                            v-model.number="total[index]"
                            :label="`${$t('Total')} (${systemCurrency})*`"
                            hide-details
                            box
                            readonly
                            :data-vv-name="'Total' + (index + 1)"
                            background-color="white"
                            :error="errors.has('Total' + (index + 1))"
                            v-validate="'required|decimal:3'"
                            data-vv-delay="1000"
                          />
                        </v-flex>
                      </v-layout>
                    </v-flex>

                    <v-flex
                      xs1
                      :class="index + 1 === rowNumbers && index + 1 > 1 ? 'pt-3' : 'pa-3'"
                      style="text-align: center"
                    >
                      <v-tooltip top>
                        <v-btn label flat @click="removeRow(index)" small slot="activator" icon color="error">
                          <v-icon>delete</v-icon>
                        </v-btn>
                        <span>{{ $t('Remove this row') }}</span>
                      </v-tooltip>
                      <v-tooltip top v-if="index + 1 === rowNumbers && index + 1 > 1">
                        <v-btn label @click="addRow()" small flat slot="activator" icon>
                          <v-icon size="28" color="green">add_circle</v-icon>
                        </v-btn>
                        <span>{{ $t('Add new row') }}</span>
                      </v-tooltip>
                    </v-flex>
                  </v-layout>
                </template>
              </v-flex>

              <v-flex xs4 pa-2 pt-4>
                <v-text-field
                  v-model="form.total_amount"
                  :label="`${$t('Total Amount')} (${systemCurrency}) *`"
                  hide-details
                  box
                  readonly
                  name="Total Amount"
                  background-color="white"
                  :append-icon="form.receive_type === 'Cash' ? 'fa-hand-holding-usd' : ''"
                  @click:append="toggleMultiCurrency()"
                  :error="errors.has('Total Amount')"
                  v-validate="'required|decimal:3'"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex xs4 pa-2 pt-4>
                <v-text-field
                  v-model="form.discount_amount"
                  :label="`${$t('Discount Amount')} (${systemCurrency}) *`"
                  hide-details
                  box
                  name="Discount Amount"
                  @input="calculateNetAmount()"
                  background-color="white"
                  :error="errors.has('Discount Amount')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex xs4 pa-2 pt-4>
                <v-text-field
                  v-model="form.net_amount"
                  :label="`${$t('Net Amount')} (${systemCurrency}) *`"
                  hide-details
                  box
                  readonly
                  name="Net Amount"
                  background-color="white"
                  :error="errors.has('Net Amount')"
                  v-validate="'required|decimal:3'"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex xs4 pa-2>
                <v-text-field
                  v-if="isMultiCurrency && form.receive_type === 'Cash'"
                  v-model="form.amount_main"
                  :label="`${$t('Amount')} (${systemCurrency}) *`"
                  box
                  hide-details
                  background-color="white"
                  name="amount_main"
                  :error="errors.has('amount_main')"
                  v-validate="`required|decimal:3|min_value:0|max_value:${form.total_amount}`"
                  @input="multiCurrencyCalculation()"
                  data-vv-delay="1000"
                />
              </v-flex>

              <v-flex xs4 pa-2>
                <v-text-field
                  v-if="isMultiCurrency && form.receive_type === 'Cash'"
                  v-model="form.amount_secondary"
                  :label="`${$t('Amount')} (${systemCurrency === 'USD' ? $t('IQD') : $t('USD')}) *`"
                  box
                  :hint="`${$t('Dollar Rate')} : ${dollarRate}`"
                  persistent-hint
                  readonly
                  background-color="white"
                  name="amount_secondary"
                  :error="errors.has('amount_secondary')"
                  v-validate="'required|decimal:3|min_value:0'"
                  data-vv-delay="1000"
                />
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-title>
        <v-divider light />

        <v-card-actions class="grey lighten-3">
          <v-layout column>
            <v-flex xs12 class="pb-2 px-2" style="color: #444;"
              >- {{ $t('Fields marked with asterisk ( * ) are required.') }}</v-flex
            >

            <v-layout justify-end align-center pr-2 pl-2>
              <v-checkbox color="primary" v-model="keepForm" :label="$t('Keep this form to add another one.')" />

              <v-btn outline color="primary" @click="$emit('hidemodal')">{{ $t('Cancel') }}</v-btn>
              <v-btn type="submit" color="success" :disabled="rowNumbers < 1" :loading="btnAddLoading">
                <v-icon :right="$vuetify.rtl" :left="!$vuetify.rtl">save</v-icon>
                {{ $t('Add') }}
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'

export default {
  name: 'add-component',

  props: {
    addDialog: { type: Boolean, required: true }
  },

  data() {
    return {
      systemCurrency: localStorage.systemCurrency,
      btnAddLoading: false,
      form: {
        isFixed: '',
        bill_date: '',
        total_amount: 0,
        customer_id_fk: 1,
        receive_type: 'Cash',
        discount_amount: 0,
        net_amount: 0,
        amount_main: 0,
        amount_secondary: 0
      },
      billDateMenu: false,
      expireDateMenu: [false],
      product_id_fk: [],
      sale_detail: [],
      collection_id_fk: [],
      sale_quantity: [],
      sale_price: [],
      available_quantity: [],
      total: [],
      collections: [],
      rowNumbers: 1,
      isMultiCurrency: false,
      keepForm: false
    }
  },

  computed: {
    ...mapGetters({
      baseUrls: 'misc/getBaseUrls',
      dollarRate: 'dollarRates/getLastDollarPrice',
      fullSaleProductsList: 'products/getFullSaleProductsList',
      assetsList: 'fixedAssets/getFullTransferFixedAssetsList',
      fullCustomersList: 'customers/getSaleCustomersList',
      fullSaleManList: 'salesmen/getFullSalesmanList',
      receiveTypes: 'sales/getReceiveTypes',
      settings: 'configurations/getSystemSettings'
    }),
    hasCollection() {
      return this.settings.production_collections === 'Active'
    },
    hasDetail() {
      return this.settings.sale_detail === 'Active'
    }
  },

  watch: {
    addDialog: {
      immediate: false,
      handler() {
        if (this.addDialog) {
          this.setFullSaleProductsList()
          this.setFullAssetsList()

          this.addEscKeyListener()
          this.resetForm()
          this.keepForm = false
        }
      }
    },
    isMultiCurrency() {
      if (this.addDialog) {
        this.$validator.pause()
        this.form.amount_main = 0
        this.form.amount_secondary = 0
        this.$nextTick(() => {
          this.$validator.resume()
        })
      }
    }
  },

  methods: {
    ...mapActions({
      addSale: 'sales/addSale',
      setFullSaleProductsList: 'products/setFullSaleProductsList',
      setFullAssetsList: 'fixedAssets/updateFullTransferFixedAssetsList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    addEscKeyListener() {
      document.addEventListener('keyup', e => {
        // if the "ESC" button was pressed and this dialog is shown, hide it
        if (e.keyCode === 27 && this.addDialog) {
          this.$emit('hidemodal')
        }
      })
    },

    resetForm() {
      this.$validator.pause()
      for (let key in this.form) {
        this.form[`${key}`] = null
      }
      this.product_id_fk = []
      this.sale_detail = []
      this.collection_id_fk = []
      this.sale_quantity = []
      this.available_quantity = []
      this.sale_price = []
      this.total = [0]
      this.rowNumbers = 1
      this.form.bill_date = this.$moment()
        .format()
        .substr(0, 10)
      this.form.total_amount = 0
      this.form.discount_amount = 0
      this.form.net_amount = 0
      this.form.customer_id_fk = 1
      this.form.receive_type = 'Cash'
      this.form.amount_main = 0
      this.form.amount_secondary = 0
      this.isMultiCurrency = false

      this.$nextTick(() => {
        this.$validator.resume()
        this.$validator.reset()
        // default veutify autofocus only one time works so we have to handled in this way.
        // document.getElementById('sale-focus-me').focus()
      })
    },

    changeReceiveTypeToCash(customerId) {
      if (customerId === 1) {
        this.form.receive_type = 'Cash'
      }
    },

    findCollections(index) {
      const filteredProduct = this.fullSaleProductsList.filter(item => item.value === this.product_id_fk[index])[0]
      this.collections[index] = filteredProduct.collections
    },

    findCollection(index) {
      return this.collections[index].filter(item => item.collection_id === this.collection_id_fk[index])[0]
    },

    collectionQuantity(index) {
      return this.findCollection(index).collection_quantity
    },

    collectionPrice(index) {
      if (this.collection_id_fk[index] === undefined) {
        let k = this.fullSaleProductsList.findIndex(item => item.value === this.product_id_fk[index])
        this.sale_price[index] = Number(this.fullSaleProductsList[k].sale_price)
        return
      }
      this.sale_price[index] = this.findCollection(index).collection_price
    },

    getProductSalePriceAndQuantity(index, product) {
      if (this.form.isFixed) {
        let assetIndex = this.assetsList.findIndex(item => item.value === product)
        let availableQuantity = this.assetsList[assetIndex].quantity
        this.available_quantity[index] = Number(availableQuantity)
      } else {
        let k = this.fullSaleProductsList.findIndex(item => item.value === product)
        let salePrice = this.fullSaleProductsList[k].sale_price
        this.sale_price[index] = Number(salePrice)
        let availableQuantity = this.fullSaleProductsList[k].quantity
        this.available_quantity[index] = Number(availableQuantity)
      }
    },

    addRow() {
      this.total[this.rowNumbers] = 0
      this.rowNumbers = this.rowNumbers + 1
    },

    removeRow(index) {
      this.rowNumbers = this.rowNumbers - 1
      this.product_id_fk.splice(index, 1)
      this.collection_id_fk.splice(index, 1)
      this.sale_detail.splice(index, 1)
      this.sale_quantity.splice(index, 1)
      this.available_quantity.splice(index, 1)
      this.sale_price.splice(index, 1)
      this.total.splice(index, 1)
      this.calculateTotalAmount()
      if (this.rowNumbers < 1) {
        this.form.discount_amount = 0
        this.form.net_amount = 0
      }
    },

    calculateTotal(index, quantity, thePrice) {
      let qtt = 0
      let price = 0
      let sumPrice = 0

      if (!isNaN(quantity)) {
        qtt = quantity
      }
      if (!isNaN(thePrice)) {
        price = thePrice
      }

      sumPrice = qtt * price
      sumPrice = Number(sumPrice)
      this.total[index] = sumPrice.toFixed(2)
      this.calculateTotalAmount()
    },

    calculateTotalAmount() {
      let leng = this.total.length
      let totalSum = 0

      for (let i = 0; i < leng; i++) {
        this.total[i] = Number(this.total[i])
        if (isNaN(this.total[i])) {
          this.total[i] = 0
        }
        totalSum = totalSum + this.total[i]
      }
      totalSum = Number(totalSum)
      this.form.total_amount = totalSum.toFixed(2)
      this.calculateNetAmount()
      this.multiCurrencyCalculation()
    },

    calculateNetAmount() {
      this.form.net_amount = this.form.total_amount - this.form.discount_amount
      this.form.net_amount = Number(this.form.net_amount).toFixed(2)
    },

    toggleMultiCurrency() {
      this.isMultiCurrency = !this.isMultiCurrency
      this.$nextTick(() => {
        if (this.isMultiCurrency) {
          this.multiCurrencyCalculation()
        }
      })
    },

    multiCurrencyCalculation() {
      const amountToExchange = Number(this.form.total_amount) - Number(this.form.amount_main)
      const exchangeSecondary =
        this.systemCurrency === 'USD' ? amountToExchange * this.dollarRate : amountToExchange / this.dollarRate
      this.form.amount_secondary = exchangeSecondary.toFixed(2)
    },

    validateBeforeSubmit(event) {
      this.$validator.validateAll().then(() => {
        if (!this.errors.any()) {
          this.btnAddLoading = true

          let formData = new FormData(event.target)
          formData.append('is_multi_currency', this.isMultiCurrency ? '1' : '0')
          formData.append('dollar_rate', this.dollarRate)
          formData.append('customer_id_fk', this.form.customer_id_fk)
          if (Number(this.form.sale_man_id_fk) > 0) {
            formData.append('sale_man_id_fk', this.form.sale_man_id_fk)
          }
          formData.append('total_amount', this.form.total_amount)
          formData.append('receive_type', this.form.receive_type)
          formData.append('asset_type', this.form.isFixed ? 'Fixed' : 'Current')
          formData.append('discount_amount', this.form.discount_amount)
          formData.append('net_amount', this.form.net_amount)
          formData.append('bill_date', this.form.bill_date)

          this.product_id_fk.forEach((p, index) => {
            formData.append('product_id_fk[' + [index] + ']', p)
            formData.append('detail[' + [index] + ']', this.sale_detail[index] || '')
            formData.append('collection_id_fk[' + index + ']', this.collection_id_fk[index] || '')
            if (this.collection_id_fk[index]) {
              formData.append('sale_collection_quantity[' + index + ']', this.sale_quantity[index])
              formData.append('sale_collection_price[' + index + ']', this.sale_price[index])
              formData.append(
                'sale_quantity[' + index + ']',
                (this.sale_quantity[index] * this.collectionQuantity(index)).toFixed(2)
              )
              formData.append(
                'sale_price[' + index + ']',
                (this.sale_price[index] / this.collectionQuantity(index)).toFixed(2)
              )
            } else {
              formData.append('sale_collection_quantity[' + index + ']', '')
              formData.append('sale_collection_price[' + index + ']', '')
              formData.append('sale_quantity[' + index + ']', this.sale_quantity[index])
              formData.append('sale_price[' + index + ']', this.sale_price[index])
            }
            formData.append('total[' + index + ']', this.total[index])
          })

          this.addSale(formData)
            .then(() => {
              this.btnAddLoading = false
              this.$emit('reset-datatable')
              if (this.keepForm) {
                this.resetForm()
              } else {
                this.$emit('hidemodal')
              }
            })
            .catch(() => {
              this.btnAddLoading = false
            })
        }
      })
    }
  }
}
</script>

<style scoped />
